export const DUE_SOON_DAYS_THRESHOLD = 3;
export const SUBSCRIPTION_PLANS_PATH = "/subscription-plans";

const SUBSCRIPTION_ACCESS_BLOCKED_STATUSES = new Set([
  "subscription_cancelled",
  "cancelled",
]);

export const isSubscriptionAccessBlocked = (detailedStatus) =>
  SUBSCRIPTION_ACCESS_BLOCKED_STATUSES.has(detailedStatus);

const LEGACY_PAYMENT_BANNER_STATUSES = new Set([
  "warning",
  "critical",
  "expired",
  "payment_overdue",
  "cancelled",
]);

const DOC_PAYMENT_BANNER_STATUSES = new Set([
  "subscription_suspended",
  "subscription_cancelled",
  "subscription_expired",
  "payment_overdue",
  "blocked",
]);

const getDaysRemaining = (subscriptionStatus) => {
  const days = subscriptionStatus?.days_remaining;
  if (typeof days === "number") return days;

  const billingDays =
    subscriptionStatus?.paypal_subscription?.billing_cycle_info
      ?.days_until_next_billing;
  return typeof billingDays === "number" ? billingDays : null;
};

const isTrialSubscription = (planData, subscriptionStatus) => {
  if (subscriptionStatus?.status === "trial") return true;
  if (subscriptionStatus?.plan_type === "trial") return true;
  if (subscriptionStatus?.subscription_type === "trial") return true;

  const trialInfo = planData?.trial_info;
  return Boolean(trialInfo && !trialInfo.is_trial_expired);
};

const hasPaypalSubscriptionRecord = (subscriptionStatus) =>
  Boolean(subscriptionStatus?.paypal_subscription?.subscription_id);

const resolveLegacyCta = (detailedStatus) => {
  if (detailedStatus === "payment_overdue") return "renew";
  if (detailedStatus === "cancelled") return "subscribe";
  return "upgrade";
};

const resolveDocCta = (detailedStatus) => {
  if (detailedStatus === "subscription_suspended") return "update_payment";
  if (
    detailedStatus === "subscription_cancelled" ||
    detailedStatus === "subscription_expired"
  ) {
    return "subscribe";
  }
  if (detailedStatus === "payment_overdue") return "renew";
  if (detailedStatus === "blocked") return "upgrade";
  return "upgrade";
};

const buildBannerState = ({
  detailedStatus,
  alert,
  bannerType,
  cta,
  daysRemaining = null,
}) => ({
  showBanner: true,
  detailedStatus,
  alert,
  bannerType,
  cta,
  daysRemaining,
});

/**
 * Resolves global payment banner state from plan-details API data.
 * Legacy status checks run first and are unchanged from the original DashboardLayout logic.
 */
export const getSubscriptionBannerState = (planData) => {
  if (!planData?.subscription_status) {
    return { showBanner: false };
  }

  const subscriptionStatus = planData.subscription_status;
  const detailedStatus = subscriptionStatus.detailed_status;
  const alert = planData.alerts?.[0] ?? null;
  const paypalSubscription = subscriptionStatus.paypal_subscription;
  const isPaypalActive = paypalSubscription?.is_active === true;
  const daysRemaining = getDaysRemaining(subscriptionStatus);
  const isTrial = isTrialSubscription(planData, subscriptionStatus);

  if (LEGACY_PAYMENT_BANNER_STATUSES.has(detailedStatus)) {
    return buildBannerState({
      detailedStatus,
      alert,
      bannerType: "legacy",
      cta: resolveLegacyCta(detailedStatus),
      daysRemaining,
    });
  }

  if (DOC_PAYMENT_BANNER_STATUSES.has(detailedStatus)) {
    return buildBannerState({
      detailedStatus,
      alert,
      bannerType: "doc",
      cta: resolveDocCta(detailedStatus),
      daysRemaining,
    });
  }

  if (
    detailedStatus === "active_subscription" &&
    !isTrial &&
    hasPaypalSubscriptionRecord(subscriptionStatus) &&
    isPaypalActive === false
  ) {
    return buildBannerState({
      detailedStatus: "subscription_suspended",
      alert,
      bannerType: "doc",
      cta: "update_payment",
      daysRemaining,
    });
  }

  if (
    detailedStatus === "active_subscription" &&
    isPaypalActive &&
    typeof daysRemaining === "number" &&
    daysRemaining > 0 &&
    daysRemaining <= DUE_SOON_DAYS_THRESHOLD
  ) {
    return buildBannerState({
      detailedStatus: "due_soon",
      alert,
      bannerType: "due_soon",
      cta: "view_billing",
      daysRemaining,
    });
  }

  return { showBanner: false };
};
